import type { OrderNoticeRecipient, OrderStockStatus } from './api/orderStockStatusApi';

/**
 * Recipient choice of the confirmation dialog (owner 2026-10-05, „Tomek i Emil
 * osobno”): the customer plus the people of each role who are ticked. Sent as
 * is in `confirmationNotice`; a role with a list is replaced on the server for
 * this one confirmation (an empty list = nobody of that role). Single home of
 * the defaults, the count and the card titles for both confirmation windows.
 */
export interface OrderConfirmationNoticeSelection {
  customer: boolean;
  operationsUserIds: string[];
  adminUserIds: string[];
}

/** The customer's address exists and the customer takes order e-mails: the customer notice can go out. */
export const isCustomerNoticeReachable = (status: OrderStockStatus): boolean =>
  Boolean(status.customerContact.email) && status.customerContact.statusEmailsEnabled;

const defaultIds = (people: OrderNoticeRecipient[], role: OrderNoticeRecipient['role']): string[] =>
  people.filter((person) => person.role === role && person.defaultSelected).map((person) => person.userId);

/** Ticks the dialog starts with: the WP4 recipient settings per person, the customer only when reachable. */
export const getConfirmationNoticeDefaults = (status: OrderStockStatus): OrderConfirmationNoticeSelection => ({
  customer: status.noticeDefaults.customer && isCustomerNoticeReachable(status),
  operationsUserIds: defaultIds(status.noticeRecipients, 'OPERATIONS'),
  adminUserIds: defaultIds(status.noticeRecipients, 'ADMIN'),
});

/** Who gets an e-mail: the ticked customer and every ticked person (the button's „(N)”). */
export const countNoticeRecipients = (selection: OrderConfirmationNoticeSelection): number =>
  (selection.customer ? 1 : 0) + selection.operationsUserIds.length + selection.adminUserIds.length;

const listKey = (role: OrderNoticeRecipient['role']): 'operationsUserIds' | 'adminUserIds' => (
  role === 'OPERATIONS' ? 'operationsUserIds' : 'adminUserIds'
);

export const isRecipientSelected = (selection: OrderConfirmationNoticeSelection, person: OrderNoticeRecipient): boolean =>
  selection[listKey(person.role)].includes(person.userId);

export const withRecipientSelected = (
  selection: OrderConfirmationNoticeSelection,
  person: OrderNoticeRecipient,
  checked: boolean,
): OrderConfirmationNoticeSelection => {
  const key = listKey(person.role);
  const rest = selection[key].filter((id) => id !== person.userId);
  return { ...selection, [key]: checked ? [...rest, person.userId] : rest };
};

/**
 * Card title of one person: the logistics person keeps the familiar
 * „Logistyk (Valentyna)” when she is called Valentyna or is the only OPERATIONS
 * user; everybody else is shown by name.
 */
export const noticeRecipientTitle = (
  person: OrderNoticeRecipient,
  all: OrderNoticeRecipient[],
  logisticsLabel: (firstName: string) => string,
): string => {
  const firstName = person.name.trim().split(/\s+/)[0] || person.name;
  const onlyLogistics = all.filter((candidate) => candidate.role === 'OPERATIONS').length === 1;
  return person.role === 'OPERATIONS' && (firstName.toLowerCase() === 'valentyna' || onlyLogistics)
    ? logisticsLabel(firstName)
    : person.name;
};
